import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardProps,
  Container,
  Grid,
  Stack,
  StackProps,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useAppSelector } from '../../../hooks';
import { TP_COLORS } from '../../../constants';
import { checkUserPermission } from '../../../helper';

import {
  getScheduleState,
  getUserSchedulesAsUser,
  IScheduleShortEvent,
} from '../../../store/V2/ScheduleSlice';
import { getAuthState } from '../../../store/AuthSlice';
import {
  getCurriculumsForHomePage,
  getCurriculumState,
  ICurriculumForHome,
} from '../../../store/CurriculumSlice';
import {
  getProgressV2State,
  resetProgress,
  getProgressByCurriculum,
} from '../../../store/V2/ProgressSlice';
import { getTenantsState } from '../../../store/V2/TenantsSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../store/V2/RolesSlice';

import TabSection, { getLatestSubject } from './components/TabSection';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import UpcomingClassBanner from '../../Home/components/UpcomingClassBanner';
import CurriculumSelector from './components/CurriculumSelector';
import SubjectProgressCard from './components/SubjectProgressCard';

const mainBoxStyles: SxProps = {
  paddingBottom: '64px',
  minHeight: 'calc(100vh - 64px)',
  background: `url('${process.env.PUBLIC_URL}/images/studentDashboard/bg-image.png') center/cover no-repeat`,
};

const newUserStackProps: StackProps = {
  gap: 1,
  sx: {
    height: '80px',
    borderRadius: '10px',
    color: '#F8FAFC',
    alignItems: 'center',
    justifyContent: 'center',
    background: `url('${process.env.PUBLIC_URL}/images/studentDashboard/bg-new-user.png') right/contain no-repeat, linear-gradient(103.87deg, #006C9C 7.37%, #002536 100%)`,
  },
};

const cardProps: CardProps = {
  sx: {
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0px 14px 40px 0px #6E9DB22E',
  },
};

const cardTitleProps: TypographyProps = {
  fontSize: '18px',
  fontWeight: '600',
  color: TP_COLORS.gray.darken,
  marginRight: '8px',
};

const filterOutExpiredUpcomingClasses = (item: IScheduleShortEvent) => {
  return (
    moment(item.start) > moment() ||
    (moment() >= moment(item.start) && moment() < moment(item.end))
  );
};

const sortUpcomingClasses = (a: IScheduleShortEvent, b: IScheduleShortEvent) =>
  new Date(a.start).getTime() - new Date(b.start).getTime();

const StudentDashboard: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const scheduleState = useAppSelector(getScheduleState);
  const tenantState = useAppSelector(getTenantsState);
  const progressState = useAppSelector(getProgressV2State);
  const curriculumState = useAppSelector(getCurriculumState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { progress, status: progressStatus } = progressState;
  const {
    curriculumsForHomePage: curriculumList,
    status: curriculumStatus,
  } = curriculumState;

  // State
  const [isJoinClassBannerOpen, setJoinClassBannerOpen] = useState(false);
  const [
    selectedCurriculum,
    setSelectedCurriculum,
  ] = useState<ICurriculumForHome>();

  const userScheduleData = scheduleState?.userScheduleData?.list || [];

  const upcomingClass = userScheduleData.length
    ? userScheduleData
        .filter(filterOutExpiredUpcomingClasses)
        .sort(sortUpcomingClasses)[0]
    : undefined;

  const latestSubject = getLatestSubject(progress.subjects);

  const isLoading =
    progressStatus === 'loading' || curriculumStatus === 'loading';

  useEffect(() => {
    if (!curriculumList.length) {
      dispatch(getCurriculumsForHomePage());
    }

    if (
      authUser &&
      authUser.role.name === 'Student' &&
      checkUserPermission({
        authUser,
        tenant: appTenant,
        permissions: {
          schedule: [USER_PERMISSION_INDIVIDUALS.SCHEDULE_VIEW],
        },
      })
    ) {
      dispatch(
        getUserSchedulesAsUser({
          userId: authUser._id,
          startDate: moment().subtract(3, 'hour').toISOString(),
          endDate: moment().add(14, 'days').toISOString(),
        })
      );
      setJoinClassBannerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (selectedCurriculum) {
      dispatch(getProgressByCurriculum(selectedCurriculum.name));
    }
  }, [selectedCurriculum?.name]);

  const handleCurriculumChange = (curriculum: string) => {
    dispatch(resetProgress());

    const findCurriculum = curriculumList.find(
      (item) => item.name === curriculum
    );

    if (findCurriculum && findCurriculum !== selectedCurriculum) {
      setSelectedCurriculum(findCurriculum);
    }
  };

  return (
    <Box sx={mainBoxStyles}>
      {upcomingClass && (
        <UpcomingClassBanner
          open={isJoinClassBannerOpen}
          onClose={() => setJoinClassBannerOpen(false)}
          classData={upcomingClass}
          user={authUser}
        />
      )}
      <Container maxWidth="lg">
        <Box display="flex" paddingTop={4} justifyContent="space-between">
          <Stack marginBottom={2}>
            <Typography
              fontSize="30px"
              fontWeight="600"
              lineHeight="40px"
              marginBottom="8px"
            >
              Hi, {authUser?.firstname} 👋
            </Typography>
            <Typography color="#475569" fontSize="18px">
              Welcome back! Let’s make progress together today. 🚀
            </Typography>
          </Stack>

          <CurriculumSelector handleCurriculumChange={handleCurriculumChange} />
        </Box>
        {isLoading ? (
          <BasicLoader
            containerProps={{
              height: '300px',
              alignItems: 'center',
            }}
          />
        ) : (
          <Grid container spacing="20px" paddingTop={3}>
            <Grid item xs={12}>
              <Stack spacing="20px">
                {latestSubject ? (
                  <Card {...cardProps}>
                    <Box display="flex" alignItems="center" marginBottom="8px">
                      <Typography {...cardTitleProps}>
                        Continue Learning
                      </Typography>
                    </Box>

                    <Typography color="#94A3B8" fontSize="14px">
                      Pick up where you left off
                    </Typography>

                    <TabSection />
                  </Card>
                ) : (
                  <Stack {...newUserStackProps}>
                    <Typography fontSize="18px" fontWeight="700">
                      Keep Learning & Exploring!
                    </Typography>
                    <Typography fontSize="14px" sx={{ opacity: '0.6' }}>
                      Select a subject to start your journey or switch your
                      curriculum anytime!
                    </Typography>
                  </Stack>
                )}

                {curriculumList.length ? (
                  <Card {...cardProps}>
                    <Box display="flex" alignItems="center" marginBottom="8px">
                      <Typography {...cardTitleProps}>All Subjects</Typography>
                      <Typography color="#94A3B8" fontSize="14px">
                        {selectedCurriculum?.name}
                      </Typography>
                    </Box>

                    <Typography color="#94A3B8" fontSize="14px">
                      Track your progress across subjects
                    </Typography>
                    <Grid
                      container
                      spacing="22px"
                      marginTop="10px"
                      marginBottom="7px"
                    >
                      {selectedCurriculum?.subjects.map(
                        (subjectItem, index) => (
                          <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                            <SubjectProgressCard
                              subjectData={{
                                ...subjectItem,
                                curriculum: selectedCurriculum.name,
                              }}
                            />
                          </Grid>
                        )
                      )}
                    </Grid>
                  </Card>
                ) : null}
              </Stack>
            </Grid>

            {/* <Grid item xs={12} md={4}>
          <Card {...cardProps}>
            <Box display="flex" alignItems="center" marginBottom="8px">
              <Typography {...cardTitleProps}>Progress Overview</Typography>
            </Box>
          </Card>
        </Grid> */}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default StudentDashboard;
